/**
 * 认证服务
 * @description 统一的认证服务入口，根据配置自动选择 Mock 或 API 适配器
 */

import type { IAuthAdapter, NormalizedLoginResult } from './types'
import type { ApiResponse } from '@/types/api'
import type {
  LoginCredentials,
  LoginResponse,
  PhoneLoginCredentials,
  RegisterCredentials,
  RegisterResponse,
  TokenInfo,
  UserInfo,
} from '@/types/auth'
import config from '@/config/api'
import { UserRole } from '@/types/auth'
import { AuthApiAdapter } from './AuthApiAdapter'
import { AuthMockAdapter } from './AuthMockAdapter'

/**
 * 认证服务类
 * 使用策略模式，根据配置选择不同的适配器实现
 */
export class AuthService {
  private adapter: IAuthAdapter

  constructor() {
    // 根据配置选择适配器
    this.adapter = config.useMockData
      ? new AuthMockAdapter()
      : new AuthApiAdapter()

    if (import.meta.env.DEV) {
      // console.log(`[AuthService] 初始化完成，使用 ${config.useMockData ? 'Mock' : 'API'} 适配器`)
    }
  }

  /**
   * 用户名密码登录
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    return this.adapter.login(credentials)
  }

  /**
   * 手机验证码登录
   */
  async phoneLogin(credentials: PhoneLoginCredentials): Promise<ApiResponse<LoginResponse>> {
    return this.adapter.phoneLogin(credentials)
  }

  async register(credentials: RegisterCredentials): Promise<ApiResponse<RegisterResponse>> {
    return this.adapter.register(credentials)
  }

  /**
   * 刷新 Token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<TokenInfo>> {
    return this.adapter.refreshToken(refreshToken)
  }

  /**
   * 登出
   */
  async logout(): Promise<ApiResponse<void>> {
    return this.adapter.logout()
  }

  /**
   * 获取当前用户信息
   */
  async getCurrentUser(): Promise<ApiResponse<UserInfo>> {
    return this.adapter.getCurrentUser()
  }

  /**
   * 规范化登录响应
   * 将不同格式的登录响应统一为标准格式
   */
  normalizeLoginResponse(
    response: ApiResponse<LoginResponse>,
    fallbackUsername?: string,
  ): NormalizedLoginResult | null {
    if (response.code !== '00000' || !response.data) {
      return null
    }

    const loginData = response.data
    // 处理 OAuth2 格式的 access_token
    const token = (loginData as LoginResponse & { access_token?: string }).access_token || loginData.token

    if (!token) {
      return null
    }

    // 构建用户信息
    const userInfo: UserInfo = loginData.userInfo || {
      id: `user_${Date.now()}`,
      username: fallbackUsername || 'unknown',
      email: fallbackUsername?.includes('@') ? fallbackUsername : `${fallbackUsername}@mech3d.com`,
      nickname: fallbackUsername || 'unknown',
      role: UserRole.USER,
      isVerified: false,
      points: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      loginCount: 1,
    }

    return {
      token,
      refreshToken: loginData.refreshToken,
      userInfo,
      permissions: loginData.permissions,
    }
  }

  /**
   * 规范化注册响应
   */
  normalizeRegisterResponse(response: ApiResponse<RegisterResponse>): NormalizedLoginResult | null {
    if (response.code !== '00000' || !response.data) {
      return null
    }

    const regData = response.data
    if (!regData.token || !regData.userInfo) {
      return null
    }

    return {
      token: regData.token,
      refreshToken: regData.refreshToken,
      userInfo: regData.userInfo,
      permissions: regData.permissions,
    }
  }
}

/**
 * 创建 AuthService 实例
 */
export function createAuthService(): AuthService {
  return new AuthService()
}

/**
 * 全局 AuthService 单例
 */
export const authService = createAuthService()
